import React, { useState } from "react";
import type { InventoryGroup, InventoryItem, UsageRecord } from "../types";
import { isToday } from "../logic/dateUtils";
import UsageRecordModal from "./modal/usageRecordModal";
import { CHEMICAL_ICON_IMAGE, CHEMICAL_MANAGEMENT_TYPE } from "../constants/const";

interface InventoryDisplayProps {
  /** 残量グループのタイトル */
  groupTitle: String;
  /** 在庫残量データ（薬剤種別ごとにグルーピング） */
  groups: InventoryGroup[];
  /** 薬剤選択時ハンドラ */
  onItemClick: (itemId: number) => void;
  /** 在庫実績入力時ハンドラ */
  onSave: (record: UsageRecord) => Promise<void>;
  /** 編集可否 */
  canEdit: boolean;
  /** 発注画面表示可否 */
  canOrder: boolean;
}

/**
 * 在庫残量表示
 * @param props
 * @returns
 */
const InventoryDisplay: React.FC<InventoryDisplayProps> = ({
  groupTitle,
  groups,
  onItemClick,
  onSave,
  canEdit,
  canOrder,
}) => {
  // 実績入力中の薬剤
  const [item, setItem] = useState<InventoryItem | null>(null);

  // 薬剤表示色取得
  const getStatusColor = (item: InventoryItem): string => {
    if (item.volume <= item.dangerThreshold) return "#ff4444";
    if (item.volume <= item.warningThreshold) return "#ffaa00";
    return "#44aa44";
  };

  // 保管貯槽表示色取得
  const getStockTankStatusColor = (
    volume: number,
    dangerThreshold: number,
    warningThreshold: number
  ) => {
    if (volume <= dangerThreshold) return "#ff4444";
    if (volume <= warningThreshold) return "#ffaa00";
    return "#44aa44";
  };

  // 在庫実績入力開始
  const handleVolumeClick = (item: InventoryItem) => {
    setItem(item);
  };

  // 在庫実績登録
  const handleVolumeSave = async (record: UsageRecord) => {
    await onSave(record);
    setItem(null);
  };

  // 薬剤アイコンイメージクラス取得
  const getIconImageClass = (iconImage: number): string => {
    return iconImage === CHEMICAL_ICON_IMAGE.TANK
      ? "tank"
      : iconImage === CHEMICAL_ICON_IMAGE.BOX
        ? "box"
        : iconImage === CHEMICAL_ICON_IMAGE.BAG
          ? "bag"
          : iconImage === CHEMICAL_ICON_IMAGE.CAN
            ? "can"
            : iconImage === CHEMICAL_ICON_IMAGE.CYLINDER
              ? "cylinder"
              : "";
  };

  return (
    <>
      {groups?.length > 0 && (
        <>
          <hr></hr>
          <h4>{groupTitle}</h4>
          <div className="inventory-display">
            {groups.map(
              group =>
                group.items.length > 0 && (
                  <div key={group.id} className="inventory-group">
                    <div className="parts">
                      <h4 className={`group-title group-title` + group.id}>{group.name}</h4>
                      <div className="items-row">
                        {group.items.map(item => {
                          const sumVolume =
                            item.volume +
                            (item.volumeOpenedBoxData && item.unit2Calc
                              ? item.volumeOpenedBoxData / item.unit2Calc
                              : 0);
                          const percentage = ((sumVolume ?? 0) / item.limit) * 100;

                          // 箱系＋貯槽系管理の場合の貯槽ゲージ
                          let stockPercentage: number[] = [];
                          if (item.stockVolumes && item.stockVolumes.length > 0) {
                            item.stockVolumes?.map((stockVolume, index) => {
                              stockPercentage.push(
                                ((stockVolume ?? 0) / item.stockLimits[index]) * 100
                              );
                            });
                          }

                          const statusColor = getStatusColor(item);
                          const warningLine = (item.warningThreshold / item.limit) * 100;
                          const dangerLine = (item.dangerThreshold / item.limit) * 100;

                          return (
                            <div key={item.id} className="inventory-item">
                              <div className="item-header">
                                <div className="item-name">
                                  {item.name}{" "}
                                  <span style={{ fontWeight: "normal" }}>[{item.unit}]</span>
                                </div>
                              </div>
                              {/* 残量表示 */}
                              <div style={{ display: "flex" }}>
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                  }}
                                >
                                  {/* 残量表示（イメージ）*/}
                                  <div
                                    className={"visual-indicator"}
                                    onClick={canOrder ? () => onItemClick(item.id) : undefined}
                                    style={canOrder ? { cursor: "pointer" } : {}}
                                  >
                                    <div
                                      className={`container ${getIconImageClass(item.iconImage1)}`}
                                    >
                                      <div
                                        className="fill"
                                        style={{
                                          height: `${percentage}%`,
                                          backgroundColor: statusColor,
                                        }}
                                      />
                                    </div>
                                  </div>
                                  {/* 残量表示（テキスト） */}
                                  <div
                                    className={`volume${canEdit ? " clickable" : ""}`}
                                    onClick={canEdit ? () => handleVolumeClick(item) : undefined}
                                  >
                                    <span className="current">{sumVolume ?? "-"}</span>
                                    <span className="unit">
                                      /{item.limit}
                                      {item.unit}
                                    </span>
                                  </div>
                                </div>

                                {/* 貯槽残量 */}
                                {item.type === CHEMICAL_MANAGEMENT_TYPE.BOX_AND_TANK &&
                                  item.stockVolumes &&
                                  item.stockVolumes.map((volume, index) => (
                                    <div
                                      style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "end",
                                      }}
                                    >
                                      {/* 残量表示（イメージ）*/}
                                      <div
                                        className={"visual-indicator"}
                                        onClick={canOrder ? () => onItemClick(item.id) : undefined}
                                        style={
                                          canOrder
                                            ? { cursor: "pointer", width: "30px", height: "30px" }
                                            : { width: "30px", height: "30px" }
                                        }
                                      >
                                        <div
                                          className={`container ${getIconImageClass(item.iconImage2)}`}
                                          style={{ width: "30px", height: "30px", margin: 0 }}
                                        >
                                          <div
                                            className="fill"
                                            style={{
                                              height: `${stockPercentage[index]}%`,
                                              backgroundColor: getStockTankStatusColor(
                                                volume,
                                                item.warningStockThreshold[index],
                                                item.dangerStockThreshold[index]
                                              ),
                                            }}
                                          />
                                        </div>
                                      </div>
                                      {/* 残量表示（テキスト） */}
                                      <div
                                        className={`volume${canEdit ? " clickable" : ""}`}
                                        onClick={
                                          canEdit ? () => handleVolumeClick(item) : undefined
                                        }
                                        style={{ fontSize: "1rem" }}
                                      >
                                        <span className="current">{volume ?? "-"}</span>
                                        <span className="unit">/{item.stockLimits[index]}L</span>
                                      </div>
                                    </div>
                                  ))}
                              </div>

                              {/* 残量表示：開封済み＋空き箱（テキスト） */}
                              {(item.type === CHEMICAL_MANAGEMENT_TYPE.BOX ||
                                item.type === CHEMICAL_MANAGEMENT_TYPE.BOX_AND_TANK) &&
                                item.countEmptyBox && (
                                  <div>
                                    <span className="">空き数:{item.countEmptyBox ?? "-"}</span>
                                    <span className="unit">{item.unit}</span>
                                  </div>
                                )}
                              {/* ステータス表示 */}
                              <div className="status">
                                {item.isOrdered && <div className="ordered">{"発注済"}</div>}
                                {!item.isOrdered && item.volume <= item.dangerThreshold && (
                                  <div className="noorder">{"未発注"}</div>
                                )}
                                {!isToday(item.date) && <div className="unchecked">{"未点検"}</div>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )
            )}
          </div>
        </>
      )}
      {/* 在庫実績入力モーダル */}
      {item && (
        <UsageRecordModal
          item={item}
          onSave={handleVolumeSave}
          onClose={() => {
            setItem(null);
          }}
        />
      )}
    </>
  );
};

export default InventoryDisplay;
